import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Loader,
  Flex,
  Image,
} from '@mantine/core';
import { AlertCircle, ArrowRight, Mail, RefreshCw } from 'react-feather';
import { authService } from '../../services/api/auth';
import { useAuthStore, clearSessionScopedState } from '../../stores/auth';
import { setStoredLastActivity } from '../../hooks/useIdleTimeout';
import {
  magicLinkErrorState,
  personaHomePath,
  safeRedirectPath,
} from './magicLinkRedirect';
import classes from './LoginPage.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

type VerificationState = 'idle' | 'loading' | 'success' | 'expired' | 'used' | 'invalid';
type ResendStatus = 'idle' | 'sending' | 'sent';

// MED-843: only persona-aware magic-link login routes are valid recovery
// destinations. Anything else (incl. /login, the password page) gets ignored
// so a stale or tampered ?from= can't strand a partner coach on a form they
// cannot submit.
const ALLOWED_RECOVERY_PATHS = ['/coach/login', '/partner/login', '/participant/login'] as const;
const DEFAULT_RECOVERY_PATH = '/coach/login';

export function VerifyMagicLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore(state => state.setAuth);
  const token = searchParams.get('token');
  const redirectParam = searchParams.get('redirect');
  const fromParam = searchParams.get('from');
  // MED-863: gate token redemption behind an explicit user click. Initial state is
  // 'idle' (interstitial card) when a token is present; 'invalid' when the URL
  // is malformed. The auto-firing useEffect was removed because email scanners
  // (Microsoft Defender for O365, Proofpoint URL Defense, etc.) increasingly
  // execute JS on link previews and would consume the token before delivery.
  const [verificationState, setVerificationState] = useState<VerificationState>(
    token ? 'idle' : 'invalid'
  );
  const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<ResendStatus>('idle');
  const recoveryPath = (ALLOWED_RECOVERY_PATHS as readonly string[]).includes(fromParam ?? '')
    ? (fromParam as string)
    : DEFAULT_RECOVERY_PATH;

  const verifyToken = async () => {
    if (!token) {
      setVerificationState('invalid');
      return;
    }

    setVerificationState('loading');

    try {
      const response = await authService.verifyMagicLink(token);

      // Set auth state
      clearSessionScopedState();
      // MDF-258: a new login starts a new idle window, not the old session's.
      // Only this path, a real login, may write the stamp.
      setStoredLastActivity(Date.now());
      setAuth(
        response.user,
        response.tokens.accessToken,
        response.tokens.refreshToken,
      );

      // Brief success state before redirect
      setVerificationState('success');

      // Use redirect param if it stays in the app, else the persona home
      const redirectPath =
        safeRedirectPath(redirectParam, window.location.origin) ??
        personaHomePath(response.user.personas);

      // Small delay to show success state
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 1000);
    } catch (error) {
      // MED-843: when the API surfaces a recovery email in error.meta
      // (used/expired states only — never for not_found probes), capture
      // it so the recovery button can resend without re-prompting.
      const errorWithMeta = error as Error & {
        code?: string;
        meta?: { recovery_email?: string; already_signed_in?: boolean };
      };

      // MDF-258: links are single use and expire. The API sets
      // already_signed_in only when this browser's own valid session is for
      // the link's user, so open the link's page in that session. No new
      // login happens here, and the stamp is not touched: the idle check on
      // the next page decides whether the session is still live.
      if (errorWithMeta.meta?.already_signed_in && useAuthStore.getState().isAuthenticated) {
        navigate(
          safeRedirectPath(redirectParam, window.location.origin) ??
            personaHomePath(useAuthStore.getState().user?.personas),
          { replace: true }
        );
        return;
      }
      if (errorWithMeta.meta?.recovery_email) {
        setRecoveryEmail(errorWithMeta.meta.recovery_email);
      }

      setVerificationState(magicLinkErrorState(errorWithMeta.code));
    }
  };

  // MED-843: trigger a fresh magic-link send for the user we just identified.
  // Uses the existing /api/auth/magic-link/request endpoint so rate limiting
  // and enumeration protection apply uniformly.
  const handleResend = async () => {
    if (!recoveryEmail || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await authService.requestMagicLink(recoveryEmail);
      setResendStatus('sent');
    } catch {
      // The apiClient interceptor surfaces 429 / server errors via toast
      // notifications; reset to idle so the user can retry.
      setResendStatus('idle');
    }
  };

  const renderRecoveryButton = () =>
    recoveryEmail ? (
      <Button
        onClick={handleResend}
        loading={resendStatus === 'sending'}
        fullWidth
        leftSection={<RefreshCw size={16} />}
      >
        Request a new link
      </Button>
    ) : (
      <Button
        component={Link}
        to={recoveryPath}
        fullWidth
        leftSection={<RefreshCw size={16} />}
      >
        Request a new link
      </Button>
    );

  const renderResentState = () => (
    <Stack align="center" gap="md">
      <Box
        p="md"
        style={{
          backgroundColor: 'var(--mantine-color-green-0)',
          borderRadius: '50%',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--mantine-color-green-6)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </Box>
      <Title order={3} ta="center">
        Check your email
      </Title>
      <Text c="dimmed" ta="center">
        If an account exists for {recoveryEmail}, we&apos;ve sent a new magic link.
      </Text>
      <Text c="dimmed" ta="center" size="sm">
        The link expires in 15 minutes. Check your spam folder if you don&apos;t see it.
      </Text>
    </Stack>
  );

  const renderContent = () => {
    switch (verificationState) {
      case 'idle':
        return (
          <Stack align="center" gap="md">
            <Box
              p="md"
              style={{
                backgroundColor: 'var(--mantine-color-gray-1)',
                borderRadius: '50%',
              }}
            >
              <Mail size={32} color="var(--mantine-color-medleyGreen-6)" />
            </Box>
            <Title order={3} ta="center">
              Welcome to Medley
            </Title>
            <Stack gap="xs" w="100%" mt="md">
              <Button
                onClick={verifyToken}
                fullWidth
                variant="outline"
                color="medleyGreen"
                rightSection={<ArrowRight size={16} />}
              >
                Continue to Medley
              </Button>
              {/* MED-1394: say why the extra click exists. */}
              <Text c="dimmed" ta="center" size="sm">
                This extra click stops email security scanners from using up your link.
              </Text>
            </Stack>
          </Stack>
        );

      case 'loading':
        return (
          <Stack align="center" gap="md">
            <Loader size="lg" color="medleyGreen" />
            <Title order={3} ta="center">
              Verifying your link...
            </Title>
            <Text c="dimmed" ta="center">
              Please wait while we log you in.
            </Text>
          </Stack>
        );

      case 'success':
        return (
          <Stack align="center" gap="md">
            <Box
              p="md"
              style={{
                backgroundColor: 'var(--mantine-color-green-0)',
                borderRadius: '50%',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--mantine-color-green-6)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </Box>
            <Title order={3} ta="center">
              You're logged in!
            </Title>
            <Text c="dimmed" ta="center">
              Redirecting to your dashboard...
            </Text>
          </Stack>
        );

      case 'expired':
        if (resendStatus === 'sent') return renderResentState();
        return (
          <Stack align="center" gap="md">
            <Box
              p="md"
              style={{
                backgroundColor: 'var(--mantine-color-orange-0)',
                borderRadius: '50%',
              }}
            >
              <AlertCircle size={32} color="var(--mantine-color-orange-6)" />
            </Box>
            <Title order={3} ta="center">
              Link expired
            </Title>
            <Text c="dimmed" ta="center">
              This magic link has expired. Please request a new one.
            </Text>
            <Stack gap="xs" w="100%" mt="md">
              {renderRecoveryButton()}
            </Stack>
          </Stack>
        );

      case 'used':
        if (resendStatus === 'sent') return renderResentState();
        return (
          <Stack align="center" gap="md">
            <Box
              p="md"
              style={{
                backgroundColor: 'var(--mantine-color-orange-0)',
                borderRadius: '50%',
              }}
            >
              <AlertCircle size={32} color="var(--mantine-color-orange-6)" />
            </Box>
            <Title order={3} ta="center">
              Link already used
            </Title>
            <Text c="dimmed" ta="center">
              This magic link has already been used. Each link can only be used
              once for security reasons.
            </Text>
            <Stack gap="xs" w="100%" mt="md">
              {renderRecoveryButton()}
            </Stack>
          </Stack>
        );

      case 'invalid':
      default:
        return (
          <Stack align="center" gap="md">
            <Box
              p="md"
              style={{
                backgroundColor: 'var(--mantine-color-red-0)',
                borderRadius: '50%',
              }}
            >
              <AlertCircle size={32} color="var(--mantine-color-red-6)" />
            </Box>
            <Title order={3} ta="center">
              Link not valid
            </Title>
            <Text c="dimmed" ta="center">
              This magic link is not valid. Please request a new one.
            </Text>
            <Stack gap="xs" w="100%" mt="md">
              <Button
                component={Link}
                to={recoveryPath}
                fullWidth
                leftSection={<RefreshCw size={16} />}
              >
                Request a new link
              </Button>
            </Stack>
          </Stack>
        );
    }
  };

  return (
    <Box className={classes.wrapper}>
      <div className={classes.pattern} />
      <Flex className={classes.container} justify="center" align="center">
        <Paper className={classes.formCard} p={40} radius="lg" shadow="xl" maw={420}>
          {/* Logo */}
          <Flex justify="center" mb="xl">
            <Box
              p="sm"
              style={{
                backgroundColor: '#2D4739',
                borderRadius: 8,
                display: 'inline-block',
              }}
            >
              <Image src={medleyLogoWhite} alt="Medley" h={28} w="auto" fit="contain" />
            </Box>
          </Flex>

          {renderContent()}
        </Paper>
      </Flex>
    </Box>
  );
}

export default VerifyMagicLinkPage;
